import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { EnvelopeSimple, Copy, CaretDown, CaretUp, ArrowsClockwise, CircleNotch, X, Plus, GoogleLogo, MagnifyingGlass } from "@phosphor-icons/react";

const relTime = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export function ReceiptEmailInbox({ businessId }) {
  const [info, setInfo] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [senderInput, setSenderInput] = useState("");
  const [savingSenders, setSavingSenders] = useState(false);
  const [maxInput, setMaxInput] = useState("");
  const [savingAuto, setSavingAuto] = useState(false);
  const [gmail, setGmail] = useState(null);
  const [scanning, setScanning] = useState(false);

  const loadGmail = () => {
    api.get(`/businesses/${businessId}/gmail`).then((r) => setGmail(r.data)).catch(() => setGmail(null));
  };

  useEffect(() => {
    loadGmail();
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") {
      toast.success("Gmail connected — Rowan now scans this inbox for receipts automatically.");
      window.history.replaceState({}, "", window.location.pathname + "?tab=receipts");
    } else if (params.get("gmail") === "error") {
      toast.error("Gmail connection didn't complete. Try again.");
      window.history.replaceState({}, "", window.location.pathname + "?tab=receipts");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const connectGmail = () => {
    const token = localStorage.getItem("bk_token");
    window.location.href = `${API}/oauth/gmail/login?business_id=${businessId}&auth=${token}`;
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/gmail/scan-now`);
      toast.success(data.already_running
        ? "A scan is already in progress — new receipts will appear as Rowan books them."
        : "Scan started — Rowan is reading the inbox now. Receipts show up here as they're booked.");
      setTimeout(() => {
        loadGmail();
        api.get(`/businesses/${businessId}/receipt-inbox`).then((r) => setInfo((cur) => ({ ...cur, ...r.data }))).catch(() => {});
        setScanning(false);
      }, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed. Try again.");
      setScanning(false);
    }
  };

  const disconnectGmail = async () => {
    try {
      await api.delete(`/businesses/${businessId}/gmail`);
      toast.success("Gmail disconnected.");
      loadGmail();
    } catch {
      toast.error("Couldn't disconnect. Try again.");
    }
  };

  useEffect(() => {
    api.get(`/businesses/${businessId}/receipt-inbox`).then((r) => {
      setInfo(r.data);
      setMaxInput(r.data?.auto_confirm?.max_amount ? String(r.data.auto_confirm.max_amount) : "");
    }).catch(() => setInfo(null));
  }, [businessId]);

  if (!info || !info.address) return null;

  const copyAddress = () => {
    const fallbackCopy = () => {
      try {
        const el = document.createElement("textarea");
        el.value = info.address;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(el);
        if (ok) toast.success("Forwarding address copied");
        else toast.info(`Copy this address: ${info.address}`);
      } catch {
        toast.info(`Copy this address: ${info.address}`);
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(info.address)
        .then(() => toast.success("Forwarding address copied"))
        .catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/receipt-inbox/regenerate`);
      setInfo((cur) => ({ ...cur, address: data.address }));
      toast.success("New forwarding address created. The old one no longer works.");
    } catch {
      toast.error("Couldn't regenerate the address. Try again.");
    } finally {
      setRegenerating(false);
    }
  };

  const saveSenders = async (senders) => {
    setSavingSenders(true);
    try {
      const { data } = await api.put(`/businesses/${businessId}/receipt-inbox/allowed-senders`, { senders });
      setInfo((cur) => ({ ...cur, allowed_senders: data.allowed_senders }));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save trusted senders.");
      return false;
    } finally {
      setSavingSenders(false);
    }
  };

  const addSender = async () => {
    const email = senderInput.trim().toLowerCase();
    if (!email) return;
    const current = info.allowed_senders || [];
    if (current.includes(email)) {
      setSenderInput("");
      return;
    }
    if (await saveSenders([...current, email])) {
      setSenderInput("");
      toast.success(`${email} added as a trusted sender`);
    }
  };

  const removeSender = (email) => {
    saveSenders((info.allowed_senders || []).filter((s) => s !== email));
  };

  const saveAutoConfirm = async (enabled, maxAmount) => {
    setSavingAuto(true);
    try {
      const { data } = await api.put(`/businesses/${businessId}/receipt-inbox/auto-confirm`, { enabled, max_amount: maxAmount });
      setInfo((cur) => ({ ...cur, auto_confirm: data.auto_confirm }));
      toast.success(data.auto_confirm.enabled ? `Auto-confirm on — trusted receipts up to $${data.auto_confirm.max_amount.toFixed(2)}` : "Auto-confirm off");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the auto-confirm rule.");
    } finally {
      setSavingAuto(false);
    }
  };

  const toggleAutoConfirm = () => {
    const next = !info.auto_confirm?.enabled;
    const amount = parseFloat(maxInput) || info.auto_confirm?.max_amount || 0;
    if (next && amount <= 0) {
      toast.error("Set a dollar limit above $0 first.");
      return;
    }
    saveAutoConfirm(next, amount);
  };

  return (
    <div className="bk-card p-5" data-testid="receipt-inbox-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--bk-accent)" }}>
            <EnvelopeSimple size={20} weight="duotone" color="#fff" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-display font-bold" style={{ color: "var(--bk-text)" }}>Email receipts to Rowan</p>
              <span
                data-testid="receipt-inbox-status"
                className="text-[11px] font-semibold uppercase tracking-wide rounded-full px-2.5 py-0.5"
                style={info.configured
                  ? { background: "rgba(47,133,90,0.12)", color: "#2F855A" }
                  : { background: "rgba(232,168,56,0.16)", color: "#9A6B15" }}
              >
                {info.configured ? "Live" : "Activation pending"}
              </span>
            </div>
            <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>
              Forward a receipt email to this address — Rowan reads the attachments and files them here automatically.
            </p>
            {(info.week_summary?.count || 0) > 0 && (
              <p className="text-xs mt-1 font-semibold" style={{ color: "#2F855A" }} data-testid="receipt-inbox-week-summary">
                This week: {info.week_summary.count} receipt{info.week_summary.count === 1 ? "" : "s"} booked by email · ${Number(info.week_summary.total || 0).toFixed(2)}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 max-w-full min-w-0">
          <button
            onClick={copyAddress}
            data-testid="receipt-inbox-copy"
            className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold bg-white min-w-0"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}
          >
            <Copy size={15} weight="bold" className="shrink-0" />
            <span className="max-w-[200px] sm:max-w-[360px] truncate" title={info.address} data-testid="receipt-inbox-address">{info.address}</span>
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="receipt-inbox-toggle"
            className="w-9 h-9 rounded-full border bg-white flex items-center justify-center"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}
          >
            {expanded ? <CaretUp size={15} weight="bold" /> : <CaretDown size={15} weight="bold" />}
          </button>
        </div>
      </div>

      {!info.configured && (
        <p className="text-xs mt-3 rounded-xl border px-3 py-2" style={{ borderColor: "rgba(232,168,56,0.4)", background: "rgba(232,168,56,0.08)", color: "var(--bk-text-2)" }} data-testid="receipt-inbox-pending-note">
          Your address is reserved. Email receiving goes live once the inbound domain is verified — anything you set up now will work the moment it's activated.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-2.5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="gmail-sync-row">
        <div className="flex items-center gap-2 min-w-0">
          <GoogleLogo size={16} weight="bold" style={{ color: "var(--bk-accent)" }} />
          {gmail?.connected ? (
            <p className="text-sm truncate" data-testid="gmail-sync-status" style={{ color: "var(--bk-text)" }}>
              <span className="font-semibold">Gmail sync on</span> · {gmail.email}
              {gmail.last_scan_at ? <span style={{ color: "var(--bk-text-2)" }}> · last scan {relTime(gmail.last_scan_at)}</span> : null}
              {gmail.status === "reauth_needed" ? <span style={{ color: "#B45309" }}> · needs reconnect</span> : null}
            </p>
          ) : (
            <p className="text-sm" data-testid="gmail-sync-status" style={{ color: "var(--bk-text-2)" }}>
              Prefer no forwarding? Connect Gmail and Rowan scans that inbox for receipts every 30 minutes.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {gmail?.connected ? (
            <>
              <button
                onClick={scanNow}
                disabled={scanning}
                data-testid="gmail-scan-now"
                className="inline-flex items-center gap-1.5 rounded-full border bg-white px-4 py-1.5 text-xs font-semibold disabled:opacity-60"
                style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}
              >
                {scanning ? <CircleNotch size={13} weight="bold" className="animate-spin" /> : <MagnifyingGlass size={13} weight="bold" />}
                {scanning ? "Scanning…" : "Scan now"}
              </button>
              <button
                onClick={disconnectGmail}
                data-testid="gmail-disconnect"
                className="rounded-full px-3 py-1.5 text-xs font-semibold"
                style={{ color: "var(--bk-text-2)" }}
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={connectGmail}
              disabled={!gmail?.configured}
              data-testid="gmail-connect-btn"
              className="bk-btn-primary px-4 py-1.5 text-xs font-semibold disabled:opacity-50"
            >
              Connect Gmail
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4" data-testid="receipt-inbox-details">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--bk-text-2)" }}>Trusted senders</p>
            <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>
              Only receipts from these emails get booked. Leave empty to accept from anyone.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {(info.allowed_senders || []).map((email) => (
                <span key={email} className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-xs font-medium" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} data-testid={`receipt-inbox-sender-${email}`}>
                  {email}
                  <button type="button" onClick={() => removeSender(email)} disabled={savingSenders} data-testid={`receipt-inbox-remove-sender-${email}`} className="disabled:opacity-50" style={{ color: "var(--bk-text-2)" }}>
                    <X size={11} weight="bold" />
                  </button>
                </span>
              ))}
              <form
                onSubmit={(e) => { e.preventDefault(); addSender(); }}
                className="inline-flex items-center gap-1.5"
              >
                <input
                  value={senderInput}
                  onChange={(e) => setSenderInput(e.target.value)}
                  placeholder="name@company.com"
                  data-testid="receipt-inbox-sender-input"
                  className="rounded-full border bg-white px-3 py-1.5 text-xs outline-none w-[190px]"
                  style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
                />
                <button
                  type="submit"
                  disabled={savingSenders || !senderInput.trim()}
                  data-testid="receipt-inbox-add-sender"
                  className="inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                  style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}
                >
                  {savingSenders ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <Plus size={12} weight="bold" />} Add
                </button>
              </form>
            </div>
          </div>

          <div data-testid="receipt-inbox-auto-confirm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--bk-text-2)" }}>Auto-confirm rule</p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>
                  {(info.allowed_senders || []).length === 0
                    ? "Add at least one trusted sender to use auto-confirm."
                    : "Receipts from trusted senders at or under this amount get confirmed and posted automatically."}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1.5" style={{ borderColor: "var(--bk-border)" }}>
                  <span className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>$</span>
                  <input
                    value={maxInput}
                    onChange={(e) => setMaxInput(e.target.value.replace(/[^0-9.]/g, ""))}
                    onBlur={() => { if (info.auto_confirm?.enabled && parseFloat(maxInput) > 0) saveAutoConfirm(true, parseFloat(maxInput)); }}
                    placeholder="50"
                    data-testid="receipt-inbox-auto-confirm-max"
                    className="w-[64px] text-xs outline-none bg-transparent"
                    style={{ color: "var(--bk-text)" }}
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleAutoConfirm}
                  disabled={savingAuto || (info.allowed_senders || []).length === 0}
                  data-testid="receipt-inbox-auto-confirm-toggle"
                  className="rounded-full px-4 py-1.5 text-xs font-semibold border disabled:opacity-50"
                  style={info.auto_confirm?.enabled
                    ? { background: "#2F855A", borderColor: "#2F855A", color: "#fff" }
                    : { background: "#fff", borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}
                >
                  {savingAuto ? "Saving…" : info.auto_confirm?.enabled ? "On" : "Off"}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--bk-text-2)" }}>Recently emailed in</p>
            <button
              onClick={regenerate}
              disabled={regenerating}
              data-testid="receipt-inbox-regenerate"
              className="inline-flex items-center gap-1.5 text-xs font-semibold disabled:opacity-60"
              style={{ color: "var(--bk-accent)" }}
            >
              {regenerating ? <CircleNotch size={13} weight="bold" className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />} New address
            </button>
          </div>
          {(info.recent || []).length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="receipt-inbox-empty">
              Nothing yet. Forward a receipt email with a photo or PDF attached and it will show up here.
            </p>
          ) : (
            <div className="space-y-2">
              {info.recent.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid={`receipt-inbox-entry-${entry.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "var(--bk-text)" }}>{entry.subject || "(no subject)"}</p>
                    <p className="text-xs truncate" style={{ color: "var(--bk-text-2)" }}>{entry.from_email || "unknown sender"} · {relTime(entry.created_at)}</p>
                  </div>
                  <span className="text-xs font-semibold shrink-0" style={{ color: entry.status === "blocked_sender" ? "#B45309" : entry.receipts_created > 0 ? "#2F855A" : "var(--bk-text-2)" }}>
                    {entry.status === "blocked_sender"
                      ? "blocked sender"
                      : entry.receipts_created > 0
                        ? `${entry.receipts_created} receipt${entry.receipts_created === 1 ? "" : "s"} booked${entry.auto_confirmed_count > 0 ? ` · ${entry.auto_confirmed_count} auto-confirmed` : ""}`
                        : "no receipts found"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
